import type { ReactNode } from 'react'
import { EmptyState } from '@/components/EmptyState'
import { Surface } from '@/components/ui/surface'
import { cn } from '@/lib/utils'

export interface ChartCardProps {
  title: string
  /** What the card shows, one line under the title (U8 style). */
  definition?: string
  /** Buttons or links on the right of the header. */
  actions?: ReactNode
  /** When set, the card shows this empty state instead of its children. */
  empty?: { title: string; description?: string; action?: ReactNode } | null
  className?: string
  children: ReactNode
}

/** A titled card for a chart or a widget: header with definition and actions, then the body or an empty state. */
export function ChartCard({ title, definition, actions, empty, className, children }: ChartCardProps) {
  return (
    <Surface asChild variant="section" className={cn('flex h-full min-w-0 flex-col gap-3', className)}>
      <section aria-label={title}>
        <header className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-heading text-base font-semibold">{title}</h2>
            {definition ? <p title={definition} className="truncate text-xs text-muted-foreground">
                {definition}
              </p> : null}
          </div>
          {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
        </header>
        {empty ? (
          <div className="flex flex-1 flex-col items-center justify-center">
            <EmptyState variant="bare" size="compact" {...empty} />
          </div>
        ) : (
          children
        )}
      </section>
    </Surface>
  )
}
